import type { ValorantAgentRole, ValorantAgentStats } from 'generated/openapi';
import { type HTMLAttributes, memo, useMemo } from 'react';

import { analytics } from '@/features/analytics/service';
import { ValorantAgentsDisplay } from '@/features/games/valorant/components/valorantAgentsDisplay';
import { ValorantRoleIcon } from '@/features/games/valorant/components/valorantRoleIcon';
import { useGetValorantAgents } from '@/features/games/valorant/hooks/useGetValorantAgents';
import { Card } from '@/shared/components/ui/card';

export interface ProfileDetailsGameplayMostPlayedAgentsProps extends HTMLAttributes<HTMLDivElement> {
  agents: Record<string, ValorantAgentStats> | undefined;
  mainValorantAgentId: string | undefined;
}

const ROLE_OPACITIES = [1, 0.6, 0.25, 0.1] as const;
const DEFAULT_ROLE_ORDER: ValorantAgentRole[] = [
  'DUELIST',
  'INITIATOR',
  'SENTINEL',
  'CONTROLLER',
];

export const ProfileDetailsGameplayMostPlayedAgents = memo(
  function ProfileDetailsGameplayMostPlayedAgents(
    props: ProfileDetailsGameplayMostPlayedAgentsProps,
  ) {
    const { agents, mainValorantAgentId, ...rest } = props;
    const { data: agentsData } = useGetValorantAgents();

    const sortedRolesWithOpacity = useMemo(() => {
      const minOpacity = ROLE_OPACITIES[ROLE_OPACITIES.length - 1];

      // If no agents data available, use default order with min opacity
      if (!agentsData?.agents) {
        return DEFAULT_ROLE_ORDER.map(role => ({
          role,
          opacity: minOpacity,
        }));
      }

      // Create Map for O(1) agent lookups
      const agentsById = new Map(
        agentsData.agents.map(a => [a.uuid.toLowerCase(), a]),
      );

      // Calculate total match count per role
      const roleMatchCounts: Record<ValorantAgentRole, number> = {
        CONTROLLER: 0,
        DUELIST: 0,
        INITIATOR: 0,
        SENTINEL: 0,
      };

      // If agents stats exist, aggregate match counts per role
      if (agents) {
        Object.entries(agents).forEach(([agentId, stats]) => {
          const agent = agentsById.get(agentId.toLowerCase());
          if (agent) {
            roleMatchCounts[agent.role] += stats.matchCount;
          } else {
            analytics.captureEvent('Agent Not Found Error Occurred', {
              agentUuid: agentId,
            });
          }
        });
      }

      const totalMatches = Object.values(roleMatchCounts).reduce(
        (sum, count) => sum + count,
        0,
      );

      // If no matches at all, use default order with min opacity
      if (totalMatches === 0) {
        return DEFAULT_ROLE_ORDER.map(role => ({
          role,
          opacity: minOpacity,
        }));
      }

      // Sort roles by match count (descending), with default order as tiebreaker
      const sortedRoles = (
        Object.entries(roleMatchCounts) as [ValorantAgentRole, number][]
      ).sort((a, b) => {
        const countDiff = b[1] - a[1];
        if (countDiff !== 0) return countDiff;
        // Tiebreaker: use default order
        return (
          DEFAULT_ROLE_ORDER.indexOf(a[0]) - DEFAULT_ROLE_ORDER.indexOf(b[0])
        );
      });

      // Apply opacities: tiered for roles with matches, min for roles without
      let opacityIndex = 0;
      return sortedRoles.map(([role, count]) => ({
        role,
        opacity:
          count > 0
            ? (ROLE_OPACITIES[opacityIndex++] ?? minOpacity)
            : minOpacity,
      }));
    }, [agents, agentsData]);

    const sortedAgents = useMemo(() => {
      if (!agents) return [];

      // Convert object to array, sorted by matchCount descending
      return Object.entries(agents)
        .sort((a, b) => b[1].matchCount - a[1].matchCount)
        .map(([id, stats]) => ({
          id,
          count: stats.matchCount,
        }));
    }, [agents]);

    return (
      <Card variant="data" {...rest}>
        <div className="flex items-center justify-between">
          <p className="text-sm leading-5 font-bold">Most Played Agents</p>
          <div className="flex gap-1">
            {sortedRolesWithOpacity.map(({ role, opacity }) => (
              <div key={role} style={{ opacity }}>
                <ValorantRoleIcon
                  role={role}
                  className="text-cold-100 size-4"
                />
              </div>
            ))}
          </div>
        </div>

        <ValorantAgentsDisplay
          agents={sortedAgents}
          mainValorantAgentId={mainValorantAgentId}
        />
      </Card>
    );
  },
);
